import * as React from 'react';
import { createUseStyles } from 'react-jss';
import { Theme } from 'helpers/theme';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faUpload } from '@fortawesome/pro-regular-svg-icons';
import { SecondaryTitle } from 'components/core/Typography';
import Spacer from 'sharedComponents/core/Spacers/Spacer';
import { commitMutation, graphql } from 'relay-runtime';
import environment from 'api/environment';
import { GraphError } from 'types/general';
import { faFileImage, faFilePdf } from '@fortawesome/pro-duotone-svg-icons';
import {
  UploadFilesMutationResponse,
  UploadFilesMutationVariables
} from './__generated__/UploadFilesMutation.graphql';
import {
  UploadFiles_AcceptMutationResponse,
  UploadFiles_AcceptMutationVariables
} from './__generated__/UploadFiles_AcceptMutation.graphql';
import { faDownload } from '@fortawesome/pro-solid-svg-icons';

const useStyles = createUseStyles((theme: Theme) => ({
  input: {
    display: 'none'
  },
  boxTitle: {
    fontSize: 15,
    marginTop: 10,
    fontWeight: 700,
    textAlign: 'center'
  },
  boxDescription: {
    color: '#6A728F',
    fontSize: 13,
    textAlign: 'center',
    fontWeight: 400
  },
  inputBox: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px dashed #D8D9DA',
    background: '#FBFCFE',
    padding: 20,
    gap: '8px',
    borderRadius: 4,
    cursor: 'pointer',
    '&:hover': {
      background: '#f7f7f7'
    }
  },
  filesContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  },
  file: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 10,
    borderRadius: 4
  },
  fileInfo: {
    gap: '13px',
    display: 'flex',
    alignItems: 'center'
  },
  fileIcon: {
    width: 25,
    height: '100%'
  },
  fileText: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2
  },
  fileName: {
    fontSize: 13,
    fontWeight: 700
  },
  fileSize: {
    fontSize: 12,
    fontWeight: 300
  },
  noneYet: {
    color: '#6A728F',
    fontSize: 13,
    fontWeight: 400
  },
  delete: {
    width: 35,
    height: 35,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    borderRadius: 6,
    '&:hover': {
      background: '#f0f0f2'
    }
  }
}));

const mutation = graphql`
  mutation UploadFilesMutation($input: UploadFileToVerifyInput!) {
    uploadFileToVerify(input: $input) {
      successToken
      url
    }
  }
`;

const acceptMutation = graphql`
  mutation UploadFiles_AcceptMutation($input: AcceptFileToVerifyInput!) {
    acceptFileToVerify(input: $input)
  }
`;

const removeMutation = graphql`
  mutation UploadFiles_RemoveMutation($uuid: UUID!) {
    removeFileToVerify(uuid: $uuid)
  }
`;

export type FileUpload = {
  id: string;
  rawFile: File;
  successToken?: string;
  uploaded?: boolean;
  uuid?: string;
  accepted?: boolean;
  extraContent?: any;
};

type Props = {
  delegateUUID: string;
  automatedVerificationID?: string;
  fileStage: string;
  files: FileUpload[]; // Files that have already been uploaded
  onUpdateFiles: (files: FileUpload[]) => void; // Called when files are uploaded, or removed
  title: string;
  description: any;
  extraContent?: any;
};

function UploadFiles(props: Props) {
  const classes = useStyles();
  console.log('Render files, ', props.files);

  const [dragging, setDragging] = React.useState(false);

  const uploadFile = (file: FileUpload): Promise<FileUpload> => {
    return new Promise((resolve, reject) => {
      const split = file.rawFile.name.split('.');
      const fType = split[split.length - 1];

      if (!props.automatedVerificationID) {
        return reject('No verification ID');
      }

      const vars: UploadFilesMutationVariables = {
        input: {
          delegateUUID: props.delegateUUID,
          automatedVerificationID: props.automatedVerificationID,
          fileStage: props.fileStage,
          filename: file.rawFile.name,
          uploadMeta: {
            fileType: fType,
            contentLength: file.rawFile.size
          }
        }
      };
      commitMutation(environment, {
        mutation,
        variables: vars,
        onCompleted: async (
          response: UploadFilesMutationResponse,
          errors: GraphError[]
        ) => {
          if (errors) {
            // Display error
            console.log(`${errors[0]?.extensions?.message}`);
            reject('Unable to upload - graph erro');
          }
          console.log('Response received from server.', response, errors);
          const data = response?.uploadFileToVerify;

          // Upload to S3
          const uploadResp = await fetch(data.url, {
            method: 'PUT',
            body: file.rawFile
          });

          if (uploadResp.status !== 200) {
            console.log('Unable to upload');
            // setUploadText('Unable to upload, try again');
            reject('Unable to upload');
          }

          file.successToken = data.successToken;
          file.uploaded = true;

          resolve({
            ...file
          });

          // onUploaded(data.successToken, URL.createObjectURL(file));
        },
        onError: (err) => console.error(err)
      });
    });
  };

  const acceptUpload = (file: FileUpload): Promise<FileUpload> => {
    return new Promise((resolve, reject) => {
      if (!props.automatedVerificationID) {
        return reject('No verification ID');
      }

      if (!file.successToken) {
        return reject('No success token');
      }

      const vars: UploadFiles_AcceptMutationVariables = {
        input: {
          automatedVerificationID: props.automatedVerificationID,
          token: file.successToken,
          filestage: props.fileStage,
          filename: file.rawFile.name
        }
      };
      commitMutation(environment, {
        mutation: acceptMutation,
        variables: vars,
        onCompleted: async (
          response: UploadFiles_AcceptMutationResponse,
          errors: GraphError[]
        ) => {
          if (errors) {
            // Display error
            console.log(`${errors[0]?.extensions?.message}`);
            reject('Unable to upload - graph erro');
          }
          console.log('Response received from server.', response, errors);
          const data = response?.acceptFileToVerify;

          file.accepted = true;
          file.uuid = data;

          resolve(file);

          // onUploaded(data.successToken, URL.createObjectURL(file));
        },
        onError: (err) => console.error(err)
      });
    });
  };

  const removeFile = async (file: FileUpload) => {
    if (!file.uuid) {
      // Remove file by ID instead
      const files = props.files.filter((f) => f.id !== file.id);
      props.onUpdateFiles(files);
      return;
    }

    return new Promise((resolve, reject) => {
      const vars = {
        uuid: file.uuid
      };
      commitMutation(environment, {
        mutation: removeMutation,
        variables: vars,
        onCompleted: async (
          response: UploadFiles_AcceptMutationResponse,
          errors: GraphError[]
        ) => {
          if (errors) {
            // Display error
            console.log(`${errors[0]?.extensions?.message}`);
            reject('Unable to upload - graph error');
          }

          console.log('Response received from server.', response, errors);
          const data = response?.acceptFileToVerify;

          const files = props.files.filter((f) => f.uuid !== file.uuid);
          props.onUpdateFiles(files);
          resolve(data);

          // onUploaded(data.successToken, URL.createObjectURL(file));
        },
        onError: (err) => console.error(err)
      });
    });
  };

  const uploadFiles = async (newFiles: File[]) => {
    let files = props.files;
    for (let i = 0; i < newFiles.length; i++) {
      const file: FileUpload = {
        rawFile: newFiles[i],
        id: `file-${Math.random()}`
      };

      const uploadedFile = await uploadFile(file);
      console.log('uploadedfile', uploadedFile);

      const acceptedFile = await acceptUpload(uploadedFile);
      console.log('acceptedFile', acceptedFile);

      if (acceptedFile && uploadedFile) {
        files = [...files, acceptedFile];
        props.onUpdateFiles(files);
      } else {
        console.error('UploadFile: Unable to upload file');
      }
    }
    try {
    } catch (err) {
      console.error('UploadFile: ' + err);
      // setUploadText('Unable to upload, please try again');
    }
  };

  const uploadChange = async (evt: React.ChangeEvent<HTMLInputElement>) => {
    // Attempt to get upload request
    if (!evt.target.files) {
      console.error('UploadFile: Could not find target file');
      return;
    }

    const newFiles = evt.target.files;
    await uploadFiles(Array.from(newFiles));
  };

  const handleDrop = async (event: React.DragEvent<HTMLLabelElement>) => {
    setDragging(false);
    event.preventDefault();
    if (event.dataTransfer.items) {
      await uploadFiles(Array.from(event.dataTransfer.files));
    }
  };

  const randomId = `random-${Math.random()}`;

  return (
    <>
      <input
        accept={'.jpg,.png,.pdf'}
        className={classes.input}
        id={'contained-button-file-' + randomId}
        multiple
        onChange={(evt) => {
          uploadChange(evt);
        }}
        type="file"
      />
      <label
        htmlFor={'contained-button-file-' + randomId}
        className={classes.inputBox}
        onDrop={handleDrop}
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDragEnd={() => setDragging(false)}
      >
        <FontAwesomeIcon
          icon={dragging ? faDownload : faUpload}
          size="2x"
          color="#205BF5"
        />
        <div className={classes.boxTitle}>
          {dragging ? 'Drop your file here' : props.title}
        </div>
        <div className={classes.boxDescription}>{props.description}</div>
      </label>
      {props.extraContent ? (
        <Spacer vertical spacing={3} />
      ) : (
        <Spacer vertical spacing={3} />
      )}
      {props.extraContent}
      {props.extraContent && <Spacer vertical spacing={3} />}
      <SecondaryTitle>Uploaded files</SecondaryTitle>
      <Spacer vertical spacing={1} />
      <div className={classes.filesContainer}>
        {props.files.length == 0 && (
          <div className={classes.noneYet}>No files yet</div>
        )}
        {props.files?.map((file) => (
          <>
            <div className={classes.file}>
              <div className={classes.fileInfo}>
                <FontAwesomeIcon
                  icon={
                    file.rawFile.type.startsWith('image/')
                      ? faFileImage
                      : faFilePdf
                  }
                  className={classes.fileIcon}
                  color="#EC3737"
                />
                <div className={classes.fileText}>
                  <div className={classes.fileName}>{file.rawFile.name}</div>
                  <div className={classes.fileSize}>file size</div>
                </div>
              </div>
              <div
                className={classes.delete}
                onClick={() => {
                  removeFile(file);
                }}
              >
                <FontAwesomeIcon icon={faTrash} />
              </div>
            </div>
          </>
        ))}
      </div>
    </>
  );
}

export default UploadFiles;
